import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import RichTextEditor from 'components/RichTextEditor';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';

import './styles.scss';
import {
  formatDateTime,
  intervalToDuration,
  isArray,
  toastStyle,
} from 'utils/helper';
import { shootingLocations } from 'utils/constants';
import {
  currencyOptionFor,
  currencyOptions,
  normalizeCurrencyCode,
} from 'utils/currencies';

const BudgetSettings = () => {
  const {
    createBudgetMutation,
    refetchBudgets,
    showBudgetSettings,
    hideBudgetSettingsModal,
    selectedBudget,
    refetchTabs,
    clearBudgetRename,
    budgetData,
    refetchBudgetId,
    hasEditAccess,
    getBudgetType,
  } = useHomeScreenContext();
  const budgetSettingsForm = useRef();
  const budgetTemplateName = getBudgetType?.() || 'Blank Budget (No Template)';
  const defaultValues = useMemo(
    () => ({
      name: selectedBudget?.name || '',
      company_name: selectedBudget?.company_name || '',
      shooting_locations:
        selectedBudget?.shooting_locations?.map(location => ({
          value: location,
          label: location,
        })) || '',
      primary_currency: currencyOptionFor(selectedBudget?.primary_currency),
      shooting_days: selectedBudget?.shooting_days || '',
      principal_photography: selectedBudget?.principal_photography || '',
      wrap_principal: selectedBudget?.wrap_principal || '',
      script_pages: selectedBudget?.script_pages || '',
      script_version: selectedBudget?.script_version || '',
      budget_version: selectedBudget?.budget_version || '',
      producers: selectedBudget?.producers?.join(', ') || '',
      director: selectedBudget?.director || '',
      production_assumptions_title:
        selectedBudget?.production_assumptions_title ||
        'Production Assumptions & Notes',
      production_assumptions: selectedBudget?.production_assumptions || '',
    }),
    [selectedBudget]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    reset,
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const productionAssumptions = watch('production_assumptions');

  useEffect(() => {
    if (showBudgetSettings) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, showBudgetSettings]);

  const handleSubmitClick = e => {
    e.preventDefault();

    budgetSettingsForm?.current?.click();
  };

  const preparePayload = data =>
    Object.keys(data)?.reduce((acc, field) => {
      const allowsEmptyValue = [
        'budget_version',
        'production_assumptions',
        'production_assumptions_title',
      ].includes(field);
      if (data[field] || allowsEmptyValue) {
        let value = data[field];
        if (field === 'producers') {
          value = value?.split(/[,\n]/)?.map(v => v?.trim());
        } else if (field === 'shooting_locations') {
          value = isArray(value) ? value?.map(v => v?.value) : [value.value];
        } else if (field === 'primary_currency') {
          value = normalizeCurrencyCode(value?.value || value);
        }
        if (field === 'production_assumptions_title' && !value?.trim?.()) {
          value = 'Production Assumptions & Notes';
        }
        acc[field] = value;
      }
      return acc;
    }, {});

  const handleBudgetSettings = () =>
    handleSubmit(async data => {
      const payload = preparePayload(data) || {};

      if (!Object.keys(payload)?.length) {
        return false;
      }

      try {
        await createBudgetMutation.mutateAsync({
          ...payload,
          id: selectedBudget?.budget,
        });
        refetchBudgets?.();
        refetchBudgetId?.();
        refetchTabs?.();
        toast.success(
          `Budget ${data?.name} ${
            selectedBudget?.budget ? 'updated' : 'created'
          } successfully`,
          toastStyle
        );
        reset();
        handleCancel();
      } catch (error) {
        const message =
          error?.response?.data?.shooting_locations?.[0] ||
          error?.response?.data?.name?.[0] ||
          error?.response?.data?.non_field_errors?.[0];
        toast.error(
          message ||
            `An error occurred while ${
              selectedBudget?.budget ? 'updating' : 'creating'
            } budget. Please try again later`,
          toastStyle
        );
      }
    });

  const handleCancel = () => {
    hideBudgetSettingsModal();
    clearBudgetRename();
    reset();
  };

  const modalTitle = () => {
    return (
      <>
        <div className="budget-settings__title">Budget Settings</div>
        <div className="budget-settings__sub-title">
          {budgetData?.name}
          <div className="created-date">
            {formatDateTime(budgetData?.created_on, 'MMMM Do, YYYY')}
          </div>
        </div>
      </>
    );
  };

  const modalFooter = () => {
    return (
      <>
        <div className="budget-settings__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="budget-settings__action__cancel"
            variant="red-outlined"
            onClick={handleCancel}
          />
          <Button
            label="Update"
            className="budget-settings__action__confirm"
            type="button"
            isDisabled={!isValid || !hasEditAccess()}
            onClick={handleSubmitClick}
          />
        </div>
        <div className="budget-settings__body__modified-info">
          <div className="budget-duration">
            Total Time Spent Editing:{' '}
            <span className="duration">
              {intervalToDuration(
                budgetData?.created_on,
                budgetData?.updated_on
              )}
            </span>
          </div>
          <div className="budget-modified">
            Last updated by{' '}
            <span className="budget-modified-by">{lastEditedBy}</span>
            {/* ,&nbsp; */}
            {formatDateTime(lastEditDateTime)}.
          </div>
        </div>
      </>
    );
  };

  const lastEditedBy =
    budgetData?.last_edited_by || budgetData?.created_by_name;
  const lastEditDateTime = budgetData?.updated_on || budgetData?.created_on;

  return (
    <Modal
      onHide={handleCancel}
      show={showBudgetSettings}
      size="sm"
      className="budget-settings"
      bodyClass="budget-settings__body"
      centered={true}
      title={modalTitle()}
      footer={modalFooter()}
      scrollable
    >
      <Form
        className="budget-settings__body__form"
        onSubmit={handleBudgetSettings()}
      >
        <div className="budget-settings__body__field budget-settings__body__field--wide">
          <FormInput
            label="Budget Name"
            type="text"
            placeholder="Please enter the budget name"
            error={errors?.name?.message}
            {...register('name', { required: 'Budget name is required' })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field budget-settings__body__field--notes">
          <FormInput
            label="Assumptions Section Heading"
            type="text"
            placeholder="Production Assumptions & Notes"
            error={errors?.production_assumptions_title?.message}
            {...register('production_assumptions_title', {
              required: false,
            })}
            disabled={!hasEditAccess()}
          />
          <input type="hidden" {...register('production_assumptions')} />
          <div className="budget-settings__assumptions-editor">
            <div className="budget-settings__assumptions-label">
              Production Assumptions & Notes
            </div>
            <RichTextEditor
              value={productionAssumptions}
              onChange={value =>
                setValue('production_assumptions', value, {
                  shouldDirty: true,
                  shouldValidate: false,
                })
              }
              disabled={!hasEditAccess()}
              placeholder="Add assumptions, notes, terms, or production context..."
              minHeight="18rem"
            />
          </div>
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Company Name"
            type="text"
            placeholder="Please enter the company name"
            error={errors?.company_name?.message}
            {...register('company_name', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormSelect
            label="Primary Currency"
            error={errors?.primary_currency?.message}
            options={currencyOptions}
            placeholder="USD ($)"
            name="primary_currency"
            rules={{
              required: false,
            }}
            control={control}
            maxMenuHeight={150}
            isSearchable
            isDisabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field budget-settings__body__field--wide">
          <FormSelect
            label="Shooting Location(s)"
            error={errors?.shooting_locations?.message}
            options={shootingLocations}
            placeholder="Los Angeles, CA"
            name="shooting_locations"
            rules={{
              required: false,
            }}
            control={control}
            maxMenuHeight={150}
            closeMenuOnSelect={false}
            isSearchable
            isMulti
            creatable
            isDisabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Director"
            type="text"
            placeholder="Director 1, Director 2"
            error={errors?.director?.message}
            {...register('director', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Producer(s)"
            type="textarea"
            placeholder="Producer 1, Producer 2"
            error={errors?.producers?.message}
            {...register('producers', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Number of Shooting Days"
            type="text"
            placeholder=""
            error={errors?.shooting_days?.message}
            {...register('shooting_days', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Script Pages"
            type="text"
            placeholder=""
            error={errors?.script_pages?.message}
            {...register('script_pages', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Script Version"
            type="text"
            placeholder=""
            error={errors?.script_version?.message}
            {...register('script_version', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Budget Version"
            type="text"
            placeholder=""
            error={errors?.budget_version?.message}
            {...register('budget_version', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Principal Photography Start Date"
            type="date"
            placeholder=""
            error={errors?.principal_photography?.message}
            {...register('principal_photography', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field">
          <FormInput
            label="Estimated Wrap Date"
            type="date"
            placeholder=""
            error={errors?.wrap_principal?.message}
            {...register('wrap_principal', { required: false })}
            disabled={!hasEditAccess()}
          />
        </div>
        <div className="budget-settings__body__field budget-settings__body__template-info">
          <div className="template-info-label">Budget Template</div>
          <div className="template-info-value">{budgetTemplateName}</div>
        </div>
        <div className="budget-settings__body__submit-proxy">
          <Button
            label="Update"
            style={{ height: 0, width: 0, opacity: 0, overflow: 'hidden' }}
            type="submit"
            ref={budgetSettingsForm}
            isDisabled={!hasEditAccess()}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default BudgetSettings;
